import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ServiceFactory } from '../../Api/Services/ServiceFactory';
import { ServiceNames } from '../../Api/registry/Services';

// Log della creazione del thunk
export const fetchPerson = createAsyncThunk(
  'person/fetchPerson',
  async (_, { rejectWithValue }) => {
    try {
      const userService = await ServiceFactory.getService(ServiceNames.UserService);
      const response = await userService.getUserMe();
      const data = response?.data ?? response;
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to fetch person data');
    }
  }
);

// legge solo i meta-dati utente (name/email/profiles ecc.)
export const fetchUserData = createAsyncThunk(
  'person/fetchUserData',
  async (args, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const currentPerson = state?.person?.person;

      const userId =
        (args && args.userId) ||
        currentPerson?.keycloakUserId;

      if (!userId) {
        return rejectWithValue('Missing userId to fetch user data');
      }

      const userService = await ServiceFactory.getService(ServiceNames.UserService);
      const response = await userService.getMyMetadata();
      const data = response?.data ?? response;
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to fetch user data');
    }
  }
);

// bundle per riallineare entrambi in un colpo
export const fetchPersonBundle = createAsyncThunk(
  'person/fetchPersonBundle',
  async (_, { dispatch, rejectWithValue }) => {
    const resPerson = await dispatch(fetchPerson());
    if (fetchPerson.rejected.match(resPerson)) {
      return rejectWithValue(resPerson.payload || 'Failed to fetch person in bundle');
    }
    const personPayload = resPerson.payload;
    const userId =
      personPayload?.keycloakUserId;

    if (userId) {
      const resUser = await dispatch(fetchUserData({ userId }));
      if (fetchUserData.rejected.match(resUser)) {
        return rejectWithValue(resUser.payload || 'Failed to fetch userData in bundle');
      }
    }
    return { ok: true };
  }
);

const initialState = {
  person: null,
  loading: false,           
  error: null,              

  // NUOVI campi per i meta-dati utente
  userData: null,
  userDataLoading: false,
  userDataError: null
};

const findCompleteCropInAgriFarms = (agriFarms, cropId) => {
  if (!agriFarms) return null;

  for (const farm of agriFarms) {
    if (!farm.parcels) continue;

    for (const parcel of farm.parcels) {
      if (!parcel.parcelOperations) continue;

      for (const operation of parcel.parcelOperations) {
        if (
          operation.cropReference &&
          typeof operation.cropReference === 'object' &&
          operation.cropReference.id === cropId
        ) {
          return operation.cropReference;
        }
      }
    }
  }
  return null;
};

const findCompleteParcelInAgriFarms = (agriFarms, parcelId) => {
  if (!agriFarms) return null;

  for (const farm of agriFarms) {
    if (!farm.parcels) continue;

    const completeParcel = farm.parcels.find(p => p.id === parcelId);
    if (completeParcel) {
      const reconstructedParcel = {
        ...completeParcel,
        farmReference: {
          id: farm.id,
          name: farm.name
        }
      };

      if (reconstructedParcel.parcelOperations) {
        reconstructedParcel.parcelOperations = reconstructedParcel.parcelOperations.map(op => {
          if (op.cropReference && typeof op.cropReference === 'string') {
            const completeCrop = findCompleteCropInAgriFarms(agriFarms, op.cropReference);
            return {
              ...op,
              cropReference: completeCrop || op.cropReference
            };
          }
          return op;
        });
      }

      return reconstructedParcel;
    }
  }
  return null;
};

const reconstructPersonData = (person) => {
  if (!person || !person.parcels || !person.agriFarms) return person;

  const updatedParcels = person.parcels.map(parcel => {
    const parcelId = typeof parcel === 'string' ? parcel : parcel.id;

    const completeParcel = findCompleteParcelInAgriFarms(person.agriFarms, parcelId);
    if (completeParcel) {
      return completeParcel;
    }

    if (parcel.parcelOperations) {
      return {
        ...parcel,
        parcelOperations: parcel.parcelOperations.map(op => {
          if (op.cropReference && typeof op.cropReference === 'string') {
            const completeCrop = findCompleteCropInAgriFarms(person.agriFarms, op.cropReference);
            return {
              ...op,
              cropReference: completeCrop || op.cropReference
            };
          }
          return op;
        })
      };
    }

    return parcel;
  });

  return {
    ...person,
    parcels: updatedParcels
  };
};

const personSlice = createSlice({
  name: 'person',
  initialState,
  reducers: {
    setPerson: (state, action) => {
      state.person = reconstructPersonData(action.payload);
    },
    // set diretto dei meta-dati (senza toccare person)
    setUserData: (state, action) => {
      state.userData = action.payload;
    }
  },
  extraReducers: (builder) => {
    // PERSON 
    builder
      .addCase(fetchPerson.pending, (state, action) => {
        state.loading = true;    
        state.error = null;      
      })
      .addCase(fetchPerson.fulfilled, (state, action) => {
        state.loading = false;   
        state.person = reconstructPersonData(action.payload);
        state.error = null;      
      })
      .addCase(fetchPerson.rejected, (state, action) => {
        state.loading = false;   
        state.error = action.payload || action.error.message;
      });

    // USER DATA
    builder
      .addCase(fetchUserData.pending, (state, action) => {
        state.userDataLoading = true;
        state.userDataError = null;
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.userDataLoading = false;
        state.userData = action.payload;
        state.userDataError = null;
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.userDataLoading = false;
        state.userDataError = action.payload || action.error.message;
      });
  }
});

export const { setPerson, setUserData } = personSlice.actions;
export default personSlice.reducer;